import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatDateUS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DateField from "../components/DateField";
import { Scales, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function JudgmentCalculator() {
    const [rates, setRates] = useState({});
    const [form, setForm] = useState({
        principal: 10000,
        judgment_date: "",
        accrual_end_date: "",
        state: "CA",
        custom_rate: "",
    });
    const [payments, setPayments] = useState([]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/calc/state-rates").then((r) => setRates(r.data)).catch(() => {});
    }, []);

    const addPayment = () => setPayments([...payments, { date: "", amount: "" }]);
    const removePayment = (i) => setPayments(payments.filter((_, idx) => idx !== i));
    const setPayment = (i, k, v) => {
        const next = [...payments];
        next[i][k] = v;
        setPayments(next);
    };

    const run = async () => {
        if (!form.judgment_date) { toast.error("Judgment date is required"); return; }
        setLoading(true);
        try {
            const payload = {
                principal: parseFloat(form.principal) || 0,
                judgment_date: form.judgment_date,
                accrual_end_date: form.accrual_end_date || null,
                state: form.custom_rate ? null : form.state,
                custom_rate: form.custom_rate ? parseFloat(form.custom_rate) : null,
                payments: payments
                    .filter((p) => p.date && p.amount)
                    .map((p) => ({ date: p.date, amount: parseFloat(p.amount) || 0 })),
            };
            const { data } = await api.post("/calc/judgment-interest", payload);
            setResult(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    return (
        <div className="space-y-8 max-w-4xl" data-testid="judgment-calc-page">
            <div>
                <p className="overline">Tools</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">Judgment balance &amp; interest</h1>
                <p className="text-slate-600 mt-3 max-w-2xl">
                    Calculate the post-judgment statutory interest on a money judgment, factoring in any payments made.
                    Choose your state for the default statutory rate or enter a custom rate.
                </p>
                <p className="text-xs text-slate-500 mt-3 italic">
                    Informational only — rates change; always verify with the court of record.
                </p>
            </div>

            <Card className="p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label htmlFor="p">Original judgment ($)</Label>
                        <Input data-testid="jcalc-principal" id="p" type="number" min="0" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} className="mt-1.5 h-11 font-mono" />
                    </div>
                    <div>
                        <Label htmlFor="jd">Judgment date *</Label>
                        <DateField data-testid="jcalc-judgment-date" id="jd" value={form.judgment_date} onChange={(e) => setForm({ ...form, judgment_date: e.target.value })} />
                    </div>
                    <div>
                        <Label htmlFor="ad">Accrue through (default: today)</Label>
                        <DateField data-testid="jcalc-end-date" id="ad" value={form.accrual_end_date} onChange={(e) => setForm({ ...form, accrual_end_date: e.target.value })} />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <Label>State</Label>
                        <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v, custom_rate: "" })}>
                            <SelectTrigger data-testid="jcalc-state" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent className="max-h-72">
                                {Object.keys(rates).sort().map((s) => (
                                    <SelectItem key={s} value={s}>{s} — {rates[s].rate}%</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {form.state && rates[form.state] && !form.custom_rate && (
                            <p className="text-xs text-slate-500 mt-2 italic">{rates[form.state].note}</p>
                        )}
                    </div>
                    <div className="md:col-span-2">
                        <Label htmlFor="cr">Custom rate (%) — overrides state</Label>
                        <Input data-testid="jcalc-custom-rate" id="cr" type="number" min="0" max="50" step="0.01" value={form.custom_rate} onChange={(e) => setForm({ ...form, custom_rate: e.target.value })} className="mt-1.5 h-11 font-mono" placeholder="e.g. 7.5" />
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-3">
                        <Label>Payments applied (interest-first)</Label>
                        <Button type="button" variant="outline" size="sm" onClick={addPayment} data-testid="jcalc-add-payment">
                            <Plus size={14} className="mr-1" /> Add payment
                        </Button>
                    </div>
                    {payments.length === 0 ? (
                        <p className="text-xs text-slate-500">No payments. Add any partial payments made by the debtor since the judgment date.</p>
                    ) : (
                        <div className="space-y-2">
                            {payments.map((p, i) => (
                                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                                    <DateField data-testid={`jcalc-payment-date-${i}`} value={p.date} onChange={(e) => setPayment(i, "date", e.target.value)} className="!mt-0 h-10" />
                                    <Input data-testid={`jcalc-payment-amount-${i}`} type="number" placeholder="Amount" min="0" value={p.amount} onChange={(e) => setPayment(i, "amount", e.target.value)} className="h-10 font-mono" />
                                    <Button type="button" variant="ghost" size="sm" onClick={() => removePayment(i)}><Trash size={14} /></Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <Button data-testid="jcalc-run-btn" onClick={run} disabled={loading} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-6">
                    <Scales size={16} className="mr-1.5" /> {loading ? "Calculating…" : "Calculate balance"}
                </Button>
            </Card>

            {result && (
                <div className="space-y-6" data-testid="jcalc-result">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
                        <Stat label="Total owed today" value={`$${result.total_owed.toLocaleString()}`} highlight />
                        <Stat label="Original principal" value={`$${result.principal.toLocaleString()}`} />
                        <Stat label="Interest accrued" value={`$${result.total_interest.toLocaleString()}`} />
                        <Stat label="Remaining principal" value={`$${result.remaining_principal.toLocaleString()}`} />
                    </div>
                    <Card className="p-6">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <p className="overline">Calculation breakdown</p>
                            <p className="text-xs text-slate-500">
                                Rate <span className="font-mono">{result.rate_pct}%</span> · {result.days_accrued} days · {result.rate_note}
                            </p>
                        </div>
                        <table className="w-full mt-4 text-sm">
                            <thead className="text-slate-500">
                                <tr>
                                    <th className="text-left py-2 font-medium">From</th>
                                    <th className="text-left py-2 font-medium">To</th>
                                    <th className="text-right py-2 font-medium">Days</th>
                                    <th className="text-right py-2 font-medium">Balance</th>
                                    <th className="text-right py-2 font-medium">Interest</th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.segments.map((s, i) => (
                                    <tr key={i} className="border-t border-slate-200">
                                        <td className="py-2 font-mono text-xs">{formatDateUS(s.from)}</td>
                                        <td className="py-2 font-mono text-xs">{formatDateUS(s.to)}</td>
                                        <td className="py-2 text-right font-mono">{s.days}</td>
                                        <td className="py-2 text-right font-mono">${s.balance_start.toLocaleString()}</td>
                                        <td className="py-2 text-right font-mono">${s.interest.toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div className={`p-6 ${highlight ? "bg-[#0F1E3D] text-white" : "bg-white"}`}>
            <p className={`overline ${highlight ? "text-white/60" : ""}`}>{label}</p>
            <p className="font-serif text-2xl mt-2 font-mono break-words">{value}</p>
        </div>
    );
}
